import React, {Component} from 'react';
import {CSSTransitionGroup as ReactCSSTransitionGroup} from 'react-transition-group';
import {observable, action, makeObservable} from 'mobx';
import {inject, observer} from 'mobx-react';
import {injectIntl} from 'react-intl';

//components
import {HeadingMobile as Heading} from 'components/Heading';
import ToolbarItem from './ToolbarItem';

//styled-components
import {Content, Fader, StyledFadingOverflow, Toolbar, ToolbarWrapper, ToolContent, StyledNoteHeader} from './styles';

//lodash
import get from 'lodash/get';
import find from 'lodash/find';

//helpers
import {richTextHasValue} from 'utils/string-utils';
import {stepNoteTypes, getCustomIconLabel} from 'utils/instruction-utils';

//messages
import messages from './messages';

@inject('store')
@observer
class ToolbarComponent extends Component {
  @observable selectedType = null;

  componentDidMount = () => {
    this.props.isCurrent && this.popupMandatoryNote();
  };

  componentDidUpdate = prevProps => {
    if (!prevProps.isCurrent && this.props.isCurrent) {
      this.popupMandatoryNote();
    }
  };

  @action
  popupMandatoryNote = () => {
    const mandatoryNoteId = get(find(this.props.notes, {mandatory: true}), 'iconId');
    mandatoryNoteId && this.showHint(mandatoryNoteId, true);
  };

  @action
  closeHint = key => {
    const {onCloseHint} = this.props;

    const type = this.selectedType;
    this.selectedType = null;

    if (typeof onCloseHint === 'function') {
      onCloseHint({key, type});
    }
  };

  @action
  showHint = (type, forced = false) => {
    if (this.selectedType === type && !forced) {
      this.closeHint('closeHintByButton');
    } else {
      const {onOpenHint} = this.props;

      const selectedNote = this.selectedNote(type);

      const selectedImageUrl = get(selectedNote, 'media.url');
      const selectedMetadata = get(selectedNote, 'media.metadata');

      this.selectedType = type;

      if (typeof onOpenHint === 'function') {
        onOpenHint({type, selectedImageUrl, selectedMetadata});
      }
    }
  };

  shouldToolbarItemBeEnabled = key => {
    const {notes, shouldAllToolbarItemsBeEnabled, store, noteContentKey} = this.props;
    const {platform} = store;
    const {canUseMediaInStepHints} = platform;

    const noteContent = find(notes, {iconId: key});
    const hasContent = richTextHasValue(get(noteContent, noteContentKey));
    const hasImageUrl = canUseMediaInStepHints && get(noteContent, 'media.url');

    return shouldAllToolbarItemsBeEnabled || hasContent || hasImageUrl;
  };

  selectedNote = iconId => find(this.props.notes, {iconId});

  getIconLabel = icon => {
    const {
      intl: {formatMessage},
      store: {
        platform: {currentLanguage}
      }
    } = this.props;
    const {type, labelTranslations = []} = icon;

    if (type !== stepNoteTypes.CUSTOM || !labelTranslations.length) {
      return formatMessage(messages[type.toLowerCase()]);
    }

    return getCustomIconLabel(labelTranslations, currentLanguage);
  };

  constructor(props) {
    super(props);
    makeObservable(this);
  }

  render() {
    const {
      store: {
        app: {theme},
        platform: {showStepNoteIconLabel}
      },
      noteContentKey
    } = this.props;

    const {icons} = theme;

    const selectedContent = get(this.selectedNote(this.selectedType), noteContentKey);
    const selectedIcon = find(icons, {id: this.selectedType});
    const selectedNoteIsWarning = get(selectedIcon, 'type') === stepNoteTypes.WARNING;

    return (
      <ToolbarWrapper>
        <ReactCSSTransitionGroup transitionName="example" transitionEnterTimeout={150} transitionLeaveTimeout={150}>
          {this.selectedType && (
            <ToolContent>
              <Fader className={'fader'} onClick={() => this.closeHint('closeHintByBackground')} />
              <StyledFadingOverflow>
                {selectedNoteIsWarning && <StyledNoteHeader>{this.getIconLabel(selectedIcon)}</StyledNoteHeader>}
                <Content className={'content'}>
                  <div data-cy="hint-content" dangerouslySetInnerHTML={{__html: selectedContent}} />
                </Content>
              </StyledFadingOverflow>
            </ToolContent>
          )}
        </ReactCSSTransitionGroup>
        <Toolbar>
          {icons.map(icon => (
            <ToolbarItem
              key={icon.id}
              icon={icon.svg}
              iconId={icon.id}
              type={icon.type.toLowerCase()}
              showHint={this.showHint}
              active={this.selectedType === icon.id}
              label={showStepNoteIconLabel && this.getIconLabel(icon)}
              themeIcon={icon.svg}
              isEnabled={this.shouldToolbarItemBeEnabled(icon.id)}
              isWarning={icon.type === stepNoteTypes.WARNING}
            />
          ))}
        </Toolbar>
      </ToolbarWrapper>
    );
  }
}

export default injectIntl(ToolbarComponent);
